import React, { createContext, useContext, useState } from "react";

import { IProviderProps, IToolbarContext } from "../@types/ContextProviderRelated";
import { EInteractionMode, ESubMenu, EWallMode } from "../@types/Enums";

/**
 * @property {ESubMenu} currentlyActiveSubMenu Enum indicating which toolbar button's dropdown, modal, or drawer is active.
 * @property {function} setCurrentlyActiveSubMenu Function to set the currentlyActiveSubMenu state.
 * @property {EInteractionMode} interactionMode Enum to indicate the current interaction mode (move, select, comment, measure, etc.).
 * @property {function} setInteractionMode Function to set the state of interactionMode.
 * @property {EWallMode} wallMode Enum indicating if one of the architecture modes is currently active.
 * @property {function} setWallMode Function to set the wallMode.
 * @property {boolean} isDrawingAllowed Flag to indicate if drawing is currently allowed.
 * @property {function} setIsDrawingAllowed Function to toggle the drawing allowed state.
 */
const toolbarContextDefaultValues: IToolbarContext = {
    currentlyActiveSubMenu: ESubMenu.NONE,
    setCurrentlyActiveSubMenu: () => {},
    interactionMode: EInteractionMode.NONE,
    setInteractionMode: () => {},
    wallMode: EWallMode.NONE,
    setWallMode: () => {},
    isDrawingAllowed: false,
    setIsDrawingAllowed: () => {},
};

export const ToolbarContext = createContext<IToolbarContext>(toolbarContextDefaultValues);

export const useToolbarContext = () => useContext(ToolbarContext);

const ToolbarProvider = ({ children }: IProviderProps): JSX.Element => {
    const [currentlyActiveSubMenu, setCurrentlyActiveSubMenu] = useState<ESubMenu>(
        toolbarContextDefaultValues.currentlyActiveSubMenu
    );
    const [interactionMode, setInteractionMode] = useState<EInteractionMode>(
        toolbarContextDefaultValues.interactionMode
    );
    const [wallMode, setWallMode] = useState<EWallMode>(toolbarContextDefaultValues.wallMode);
    const [isDrawingAllowed, setIsDrawingAllowed] = useState<boolean>(
        toolbarContextDefaultValues.isDrawingAllowed
    );

    return (
        <ToolbarContext.Provider
            value={{
                currentlyActiveSubMenu,
                setCurrentlyActiveSubMenu,
                interactionMode,
                setInteractionMode,
                wallMode,
                setWallMode,
                isDrawingAllowed,
                setIsDrawingAllowed,
            }}
        >
            {children}
        </ToolbarContext.Provider>
    );
};

export default ToolbarProvider;
